import { useCallback, useEffect, useState } from "react";
import { Check, CreditCard, RefreshCw } from "lucide-react";
import { clearWhopAccessToken, WhopAdsSurface } from "./WhopAdsSurface";
import { api } from "../lib/api";

type SavedCard = {
  id: string;
  brand: string | null;
  last4: string | null;
  primary: boolean;
  backup: boolean;
};

type BillingStatus = {
  saved_card_state?: "recoverable" | null;
  account_email?: string | null;
  saved_cards?: SavedCard[];
};

export function BillingWorkspace({ onChanged }: { onChanged: () => Promise<unknown> }) {
  const [status, setStatus] = useState<BillingStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [surfaceRevision, setSurfaceRevision] = useState(0);
  const [selectingCard, setSelectingCard] = useState("");
  const [connecting, setConnecting] = useState(false);
  const [connectError, setConnectError] = useState("");
  const loadStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      setStatus(await api<BillingStatus>("/api/ads/billing-status"));
      await onChanged();
    } catch (caught) {
      setConnectError(caught instanceof Error ? caught.message : "Saved cards could not be refreshed.");
    } finally {
      setStatusLoading(false);
    }
  }, [onChanged]);

  useEffect(() => {
    let active = true;
    void api<BillingStatus>("/api/ads/billing-status")
      .then((next) => { if (active) setStatus(next); })
      .catch(() => { if (active) setStatus(null); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    const refresh = () => { if (document.visibilityState === "visible") void loadStatus(); };
    const interval = window.setInterval(() => {
      if (document.visibilityState !== "visible") return;
      void api<BillingStatus>("/api/ads/billing-status").then(setStatus).catch(() => undefined);
    }, 5_000);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [loadStatus]);

  const refreshElement = useCallback(async () => {
    clearWhopAccessToken();
    setSurfaceRevision((value) => value + 1);
    await loadStatus();
  }, [loadStatus]);

  const connectSavedCard = async () => {
    setConnecting(true);
    setConnectError("");
    try {
      await api("/api/ads/billing-connect/recover", { method: "POST" });
      await refreshElement();
    } catch (caught) {
      setConnectError(caught instanceof Error ? caught.message : "The saved card could not be connected.");
    } finally {
      setConnecting(false);
    }
  };

  const selectSavedCard = async (card: SavedCard, slot: "primary" | "backup") => {
    setSelectingCard(`${card.id}:${slot}`);
    setConnectError("");
    try {
      await api("/api/ads/billing-method", {
        method: "POST",
        body: JSON.stringify({ payment_method_id: card.id, slot }),
      });
      await refreshElement();
    } catch (caught) {
      setConnectError(caught instanceof Error ? caught.message : "That card could not be selected for ad spend.");
    } finally {
      setSelectingCard("");
    }
  };

  const savedCard = status?.saved_card_state;
  const savedCards = status?.saved_cards || [];
  return <div className="billing-workspace">
    {savedCard === "recoverable" && <section className="billing-notice"><div><strong>Finish connecting your saved card</strong><span>Your card was saved but is not yet the payment method for ad spend.</span>{connectError && <span>{connectError}</span>}</div><button className="button button--primary button--small" type="button" onClick={() => { void connectSavedCard(); }} disabled={connecting}>{connecting ? "Connecting…" : "Connect saved card"}</button></section>}
    {savedCards.length > 0 && <section className="billing-saved-cards" aria-label="Saved payment cards">
      <div className="billing-saved-cards__header">
        <div><strong>Saved cards</strong><span>Select the card the portal should use for ad spend.</span></div>
        <button className="billing-refresh" type="button" onClick={() => { setConnectError(""); void loadStatus(); }} disabled={statusLoading} aria-label="Refresh saved cards"><RefreshCw className={statusLoading ? "spin" : ""} /> Refresh</button>
      </div>
      <div className="billing-card-list">
        {savedCards.map((card) => <article className={card.primary || card.backup ? "is-selected" : ""} key={card.id}>
          <span className="billing-card-list__icon"><CreditCard /></span>
          <div><strong>{card.brand ? card.brand.toUpperCase() : "Card"} •••• {card.last4 || "Saved"}</strong><span>{card.primary ? "Primary ad payment method" : card.backup ? "Backup ad payment method" : "Available for this account owner"}</span></div>
          <div className="billing-card-list__actions">
            {card.primary && <span className="billing-card-list__current"><Check /> Primary</span>}
            {card.backup && <span className="billing-card-list__current"><Check /> Backup</span>}
            {!card.primary && <button className="button button--small" type="button" disabled={Boolean(selectingCard)} onClick={() => { void selectSavedCard(card, "primary"); }}>{selectingCard === `${card.id}:primary` ? "Updating…" : "Use as primary"}</button>}
            {!card.primary && !card.backup && <button className="button button--primary button--small" type="button" disabled={Boolean(selectingCard)} onClick={() => { void selectSavedCard(card, "backup"); }}>{selectingCard === `${card.id}:backup` ? "Updating…" : "Use as backup"}</button>}
          </div>
        </article>)}
      </div>
      {connectError && <p className="billing-saved-cards__error" role="alert">{connectError}</p>}
    </section>}
    {savedCards.length === 0 && <div className="billing-refresh-row"><button className="billing-refresh" type="button" onClick={() => { setConnectError(""); void loadStatus(); }} disabled={statusLoading}><RefreshCw className={statusLoading ? "spin" : ""} /> Check saved cards</button>{connectError && <span role="alert">{connectError}</span>}</div>}
    <WhopAdsSurface key={surfaceRevision} surface="billing" onBillingChanged={() => { void refreshElement(); }} />
  </div>;
}
